import * as yup from 'yup';
import { ArrowUpRight, ChevronRight, User } from 'react-feather';
import { useForm } from 'react-form-state-manager';
import { useTranslation } from 'react-i18next';

import { GetMailPermissionsQuery as GetMailPermissions, Permission } from '__generated__/graphql';

import { Event } from './helpers';
import { getServerErrors } from '../../../../common/helpers';
import ProtectedSection from '../../../Common/ProtectedSection';
import UI from '../../../../common/UI';
import useEditEventMutation from './useEditEventMutation';
import useLocale from '../../../../common/useLocale';
import useNavigationPrompt from '../../../../common/useNavigationPrompt';
import useProject from '../../../useProject';
import useValidation from '../../../../common/useValidation';

type MailPermission = GetMailPermissions['project']['mail_permissions'][0];

export interface EditEventInterestListFormProps {
  event: Event;
  mailPermissions: MailPermission[];
}

const EditEventInterestListForm = ({ event, mailPermissions }: EditEventInterestListFormProps) => {
  const { t } = useTranslation();
  const { formatNumber } = useLocale();
  const project = useProject();

  const getValues = (event: Event) => ({
    enabled: event.enable_interest_list,
    mail_permission_id: event.interest_list?.id ?? mailPermissions[0]?.id,
  });

  const form = useForm({
    values: getValues(event),
  });

  const selectedPermission = mailPermissions.find(
    (mailPermission) => mailPermission.id === form.values.mail_permission_id,
  );

  const editEventInterestListSchema = yup.object().shape({
    enabled: yup.boolean(),
    mail_permission_id: yup.string().required(),
  });

  useNavigationPrompt(form.changed());

  const [editEvent, { error, loading }] = useEditEventMutation({
    input: {
      id: event.id,
      interest_list: {
        ...form.values,
      },
    },
    onSuccess: (event) => {
      form.reset(getValues(event));
    },
  });

  const { valid, errors } = useValidation({
    schema: editEventInterestListSchema,
    values: form.values,
    externalErrors: getServerErrors(error, 'interest_list'),
  });

  return (
    <UI.Form onSubmit={() => editEvent()}>
      <UI.FormGrid>
        <UI.ServerErrors error={error} />

        <UI.Div>
          <UI.Legend sc={{ mb: 1 }}>
            {t('lead_generation')}
          </UI.Legend>
          <UI.InputDescription>
            {t('lead_generation_description')}
          </UI.InputDescription>
        </UI.Div>

        <UI.InputGroup sc={{ valid: !errors.enabled, touched: form.touched.enabled }}>
          <UI.FormGrid>
            <UI.Checkbox
              {...form.checkbox('enabled')}
              sc={{ box: true }}
              id="Enabled"
            >
              <UI.Strong sc={{ block: true }}>
                {t('enable_lead_generation')}
              </UI.Strong>
              {t('enable_lead_generation_description')}
            </UI.Checkbox>
          </UI.FormGrid>
        </UI.InputGroup>

        {(form.values.enabled || event.interest_list) && (
          <UI.InputGroup
            sc={{ valid: !errors['interest_list.mail_permission'], touched: form.touched.mail_permission_id }}
          >
            <UI.InputLabel htmlFor="InterestListMailPermission">
              {t('mail_permission')}
              {form.values.enabled && (
                <>
                  {' '}
                  <UI.RequiredMark />
                </>
              )}
            </UI.InputLabel>
            <UI.InputDescription>
              {t('lead_generation_mail_permission_description')}
            </UI.InputDescription>
            {mailPermissions.length > 0 && (
              <UI.Select
                {...form.select(
                  'mail_permission_id',
                  mailPermissions.map((permission) => permission.id),
                )}
                id="InterestListMailPermission"
              >
                {mailPermissions.map((permission, index) => (
                  <option {...form.option(index)} key={index}>
                    {permission.title}
                  </option>
                ))}
              </UI.Select>
            )}
            {mailPermissions.length === 0 && (
              <UI.Warning>
                {t('no_mail_permissions_added_yet')}
                {' '}
                <UI.Link to={`/${project.id}/communication/permissions`}>
                  {t('manage_mail_permissions')}
                </UI.Link>
              </UI.Warning>
            )}
            <UI.ErrorMessages
              attribute={t('mail_permission')}
              errors={errors['interest_list.mail_permission_id']}
            />
          </UI.InputGroup>
        )}

        {event.interest_list && event.interest_list.id === form.values.mail_permission_id && (
          <UI.Info>
            {!selectedPermission?.integration && (
              <>
                <UI.Span sc={{ mr: 3 }}>
                  <UI.Icon>
                    <User />
                  </UI.Icon>
                  {' '}
                  {t('n_opt_ins_collected', {
                    count: event.interest_list.opt_in_count,
                    value: formatNumber(event.interest_list.opt_in_count),
                  })}
                </UI.Span>
                <ProtectedSection permissions={[Permission.view_registrations]}>
                  <UI.Link
                    to={`/${project.id}/participants/contacts?event=${event.id}&mail_opt_ins[]=${event.interest_list.id}`}
                    sc={{ noWrap: true, mr: 3, disabled: !event.interest_list }}
                  >
                    {t('view_opt_ins')}
                    {' '}
                    <UI.Icon>
                      <ChevronRight />
                    </UI.Icon>
                  </UI.Link>
                </ProtectedSection>
              </>
            )}
            <ProtectedSection permissions={[Permission.messages]}>
              <UI.Link
                to={`/${project.id}/communication/permissions/${event.interest_list?.id}`}
                sc={{ noWrap: true, mr: 3, disabled: !event.interest_list }}
              >
                {t('configure_mail_permission')}
                {' '}
                <UI.Icon>
                  <ChevronRight />
                </UI.Icon>
              </UI.Link>
            </ProtectedSection>
            <UI.A
              href={`/e/${event.id}/interest`}
              sc={{ noWrap: true, disabled: !event.interest_list }}
              target="_blank"
              rel="noopener noreferer"
            >
              {t('view_form')}
              {' '}
              <UI.Icon>
                <ArrowUpRight />
              </UI.Icon>
            </UI.A>
          </UI.Info>
        )}

        <UI.HR />

        <UI.FormButtons
          form={form}
          submitLabel={t('save_event')}
          loading={loading}
          valid={valid}
        />
      </UI.FormGrid>
    </UI.Form>
  );
};

export default EditEventInterestListForm;
